import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faClock } from '@fortawesome/free-solid-svg-icons';
import type { EngineerWorkload } from '@/services/dashboard.service';
import type { Department } from '@/services/department.service';
import { SearchBar } from '@/components/shared/SearchBar';

const SELECT_CLASS =
  'flex h-9 rounded-lg border border-input bg-card px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1';

type SortKey = 'name' | 'activeAsc' | 'activeDesc' | 'lastAssignment';

const ROLE_LABEL: Record<string, string> = {
  'service-engineer': 'Service Engineer',
  'master-service-engineer': 'Master Service Engineer',
};

function fmtDate(v: string | null): string {
  if (!v) return 'Never';
  return new Date(v).toLocaleDateString();
}

interface EngineerPanelProps {
  engineers: EngineerWorkload[];
  departments: Department[];
  onSelectEngineer?: (engineerId: string) => void;
}

export function EngineerPanel({ engineers, departments, onSelectEngineer }: EngineerPanelProps) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [sort, setSort] = useState<SortKey>('activeAsc');

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = engineers.filter((e) => {
      if (roleFilter && e.roleSlug !== roleFilter) return false;
      if (departmentFilter && e.departmentId !== departmentFilter) return false;
      if (term && !`${e.firstName} ${e.lastName}`.toLowerCase().includes(term)) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => {
      switch (sort) {
        case 'activeAsc':
          return a.activeJobs - b.activeJobs;
        case 'activeDesc':
          return b.activeJobs - a.activeJobs;
        case 'lastAssignment':
          return (b.lastAssignmentAt ?? '').localeCompare(a.lastAssignmentAt ?? '');
        case 'name':
        default:
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      }
    });
    return rows;
  }, [engineers, search, roleFilter, departmentFilter, sort]);

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="space-y-2 border-b border-border p-4">
        <SearchBar value={search} onChange={setSearch} placeholder="Search engineers…" />
        <div className="flex gap-2">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className={`${SELECT_CLASS} flex-1`}
            aria-label="Filter by role"
          >
            <option value="">All Roles</option>
            <option value="service-engineer">Service Engineer</option>
            <option value="master-service-engineer">Master Service Engineer</option>
          </select>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className={`${SELECT_CLASS} flex-1`}
            aria-label="Filter by department"
          >
            <option value="">All Departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className={`${SELECT_CLASS} flex-1`}
            aria-label="Sort engineers"
          >
            <option value="activeAsc">Fewest Active Jobs</option>
            <option value="activeDesc">Most Active Jobs</option>
            <option value="name">Name</option>
            <option value="lastAssignment">Last Assignment</option>
          </select>
        </div>
      </div>

      <div className="max-h-[640px] space-y-2 overflow-y-auto p-3">
        {visible.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            No engineers match your search or filters.
          </p>
        ) : (
          visible.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => onSelectEngineer?.(e.id)}
              className="w-full rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/30"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {e.firstName} {e.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {ROLE_LABEL[e.roleSlug] ?? e.roleSlug}
                    {e.departmentName && ` · ${e.departmentName}`}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {e.isRecommended && (
                    <span className="flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                      <FontAwesomeIcon icon={faStar} className="h-2.5 w-2.5" />
                      Recommended
                    </span>
                  )}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      e.isActive
                        ? 'bg-green-500/10 text-green-700'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {e.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-muted/40 py-1.5">
                  <p className="text-sm font-semibold text-foreground">{e.activeJobs}</p>
                  <p className="text-[10px] text-muted-foreground">Active</p>
                </div>
                <div className="rounded-md bg-muted/40 py-1.5">
                  <p className="text-sm font-semibold text-foreground">{e.assignedJobs}</p>
                  <p className="text-[10px] text-muted-foreground">Assigned</p>
                </div>
                <div className="rounded-md bg-muted/40 py-1.5">
                  <p className="text-sm font-semibold text-foreground">{e.pendingReviewJobs}</p>
                  <p className="text-[10px] text-muted-foreground">In Review</p>
                </div>
              </div>

              <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
                  Last assigned: {fmtDate(e.lastAssignmentAt)}
                </span>
                <span title="Availability not yet implemented — see docs/OPS_ENGINEER_ASSIGNMENT_CENTER_DESIGN.md">
                  Availability: not yet implemented
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
